import React, { useEffect, useState } from "react";
import { Portal } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import MediaUpload from "@/components/MediaUpload";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Mail, PlayCircle, FileText, ShoppingCart, MessagesSquare, Send, DollarSign } from "lucide-react";

/** ------------------------- INVITES ------------------------- */
const InvitesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try { setItems(await Portal.invites.list()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await Portal.invites.create(form);
      toast.success("Invite added");
      setShowAdd(false);
      setForm({ email: "", name: "", company: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (inv) => {
    try { await Portal.invites.update(inv.id, { is_active: !inv.is_active }); load(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Remove ${inv.email} from portal invites?`)) return;
    try { await Portal.invites.remove(inv.id); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-slate-400">Only invited emails (and admins) can access the portal.</p>
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full brand-gradient text-white text-xs font-semibold" data-testid="portal-invite-add">
          <Plus className="h-3.5 w-3.5" /> Invite user
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400" data-testid="portal-invites-empty">
          No invites yet. Add one to grant portal access.
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Company</th>
                <th className="text-left px-5 py-3">Active</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((inv) => (
                <tr key={inv.id} data-testid={`portal-invite-${inv.id}`}>
                  <td className="px-5 py-3 text-white">{inv.email}</td>
                  <td className="px-5 py-3 text-slate-300">{inv.name || "—"}</td>
                  <td className="px-5 py-3 text-slate-300">{inv.company || "—"}</td>
                  <td className="px-5 py-3"><Switch checked={inv.is_active} onCheckedChange={() => toggle(inv)} /></td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => remove(inv)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md bg-slate-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>Invite a portal user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Google email address (required)" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-slate-900 border-white/10" data-testid="portal-invite-email" />
            <Input placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-slate-900 border-white/10" data-testid="portal-invite-name" />
            <Input placeholder="Company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="bg-slate-900 border-white/10" />
            <Textarea rows={2} placeholder="Internal notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-slate-900 border-white/10" />
          </div>
          <DialogFooter>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-slate-400 text-sm hover:text-white">Cancel</button>
            <button onClick={save} disabled={!form.email} className="px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-50" data-testid="portal-invite-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** ------------------------- VIDEOS ------------------------- */
const emptyVideo = { title: "", description: "", video_url: "", thumbnail_url: "", category: "General", duration: "", sort_order: 0, is_published: true };
const VideosTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // "new" or id
  const [form, setForm] = useState(emptyVideo);

  const load = async () => {
    setLoading(true);
    try { setItems(await Portal.videos.list()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyVideo); setEdit("new"); };
  const openEdit = (v) => { setForm({ ...emptyVideo, ...v }); setEdit(v.id); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      if (edit === "new") await Portal.videos.create(form);
      else await Portal.videos.update(edit, form);
      toast.success("Saved");
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try { await Portal.videos.remove(v.id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-slate-400">Upload a video file OR paste a YouTube/Vimeo URL. Members see published only.</p>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-full brand-gradient text-white text-xs font-semibold" data-testid="portal-video-add"><Plus className="h-3.5 w-3.5" /> Add video</button>
      </div>
      {loading ? <div className="text-slate-400">Loading…</div> : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">No videos yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v) => (
            <div key={v.id} className="glass rounded-xl p-4 space-y-2" data-testid={`portal-video-${v.id}`}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-atc-blue">{v.category}</div>
              <div className="text-white font-semibold text-sm">{v.title}</div>
              {v.description && <div className="text-xs text-slate-400 line-clamp-2">{v.description}</div>}
              <div className="text-[10px] text-slate-500 truncate">{v.video_url || <span className="text-rose-400">No URL</span>}</div>
              <div className="flex items-center justify-between pt-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${v.is_published ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>
                  {v.is_published ? "Published" : "Draft"}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(v)} className="p-1.5 text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(v)} className="p-1.5 text-slate-400 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl bg-slate-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>{edit === "new" ? "Add training video" : "Edit video"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-slate-900 border-white/10" data-testid="portal-video-title" />
            <Textarea rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="bg-slate-900 border-white/10" />
            <div className="grid sm:grid-cols-3 gap-2">
              <Input placeholder="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="bg-slate-900 border-white/10" />
              <Input placeholder="Duration (12:45)" value={form.duration} onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))} className="bg-slate-900 border-white/10" />
              <Input type="number" placeholder="Sort order" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} className="bg-slate-900 border-white/10" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">Video (file, YouTube, or Vimeo URL)</div>
              <MediaUpload value={form.video_url} onChange={(url) => setForm((f) => ({ ...f, video_url: url }))} kind="any" testId="portal-video-media" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">Thumbnail (optional)</div>
              <MediaUpload value={form.thumbnail_url} onChange={(url) => setForm((f) => ({ ...f, thumbnail_url: url }))} kind="image" testId="portal-video-thumb" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))} data-testid="portal-video-publish" /> Published
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="px-4 py-2 text-slate-400 text-sm">Cancel</button>
            <button onClick={save} className="px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="portal-video-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** ------------------------- DOCS/MANUALS ------------------------- */
const emptyDoc = { title: "", description: "", file_url: "", file_type: "pdf", category: "Manuals", sort_order: 0, is_published: true };
const DocsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyDoc);

  const load = async () => {
    setLoading(true);
    try { setItems(await Portal.docs.list()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.file_url.trim()) { toast.error("Title and file URL required"); return; }
    try {
      if (edit === "new") await Portal.docs.create(form);
      else await Portal.docs.update(edit, form);
      toast.success("Saved");
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try { await Portal.docs.remove(d.id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-slate-400">Upload PDFs or other files, or paste an existing URL.</p>
        <button onClick={() => { setForm(emptyDoc); setEdit("new"); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full brand-gradient text-white text-xs font-semibold" data-testid="portal-doc-add">
          <Plus className="h-3.5 w-3.5" /> Add document
        </button>
      </div>
      {loading ? <div className="text-slate-400">Loading…</div> : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">No documents yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((d) => (
            <div key={d.id} className="glass rounded-xl p-4 flex items-start gap-3" data-testid={`portal-doc-${d.id}`}>
              <div className="h-10 w-10 rounded-lg bg-atc-blue/15 border border-atc-blue/30 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-atc-blue" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{d.category} · {(d.file_type || "PDF").toUpperCase()}</div>
                <div className="text-white text-sm font-semibold">{d.title}</div>
                <div className="text-[10px] text-slate-500 truncate">{d.file_url}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.is_published ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{d.is_published ? "Live" : "Draft"}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setForm({ ...emptyDoc, ...d }); setEdit(d.id); }} className="p-1 text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(d)} className="p-1 text-slate-400 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg bg-slate-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>{edit === "new" ? "Add document" : "Edit document"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-slate-900 border-white/10" data-testid="portal-doc-title" />
            <Textarea rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="bg-slate-900 border-white/10" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="bg-slate-900 border-white/10" />
              <Input placeholder="Type (pdf, doc)" value={form.file_type} onChange={(e) => setForm((f) => ({ ...f, file_type: e.target.value }))} className="bg-slate-900 border-white/10" />
              <Input type="number" placeholder="Sort" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} className="bg-slate-900 border-white/10" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">File</div>
              <MediaUpload value={form.file_url} onChange={(url) => setForm((f) => ({ ...f, file_url: url }))} kind="any" testId="portal-doc-file" label="Upload PDF/DOC or paste URL" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))} /> Published
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="px-4 py-2 text-slate-400 text-sm">Cancel</button>
            <button onClick={save} className="px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="portal-doc-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** ------------------------- PRODUCTS (Pricing Catalog) ------------------------- */
const emptyProduct = {
  name: "", sku: "", price: 0, unit: "each", category: "Equipment",
  description: "", image_url: "", in_stock: true, sort_order: 0, is_published: true,
};
const ProductsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { setItems(await Portal.products.list()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = { ...form, price: Number(form.price) || 0, sort_order: Number(form.sort_order) || 0 };
      if (edit === "new") await Portal.products.create(payload);
      else await Portal.products.update(edit, payload);
      toast.success("Saved");
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await Portal.products.remove(p.id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = items.filter((p) => {
    const needle = q.toLowerCase().trim();
    if (!needle) return true;
    return (p.name + " " + p.sku + " " + p.category).toLowerCase().includes(needle);
  });

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <p className="text-xs text-slate-400">Items shown in the customer reorder page with prices and estimated total.</p>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="px-3 py-1.5 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
            data-testid="portal-product-search"
          />
          <button onClick={() => { setForm(emptyProduct); setEdit("new"); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full brand-gradient text-white text-xs font-semibold" data-testid="portal-product-add">
            <Plus className="h-3.5 w-3.5" /> Add product
          </button>
        </div>
      </div>

      {loading ? <div className="text-slate-400">Loading…</div> : filtered.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">
          {items.length === 0 ? "No products yet. Add your first item so customers can pick from a catalog." : "No matches for that search."}
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">SKU</th>
                <th className="text-left px-5 py-3">Category</th>
                <th className="text-right px-5 py-3">Price</th>
                <th className="text-left px-5 py-3">Unit</th>
                <th className="text-left px-5 py-3">Stock</th>
                <th className="text-left px-5 py-3">Live</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`portal-product-${p.id}`}>
                  <td className="px-5 py-3 text-white font-medium">{p.name}</td>
                  <td className="px-5 py-3 text-slate-400 text-xs">{p.sku || "—"}</td>
                  <td className="px-5 py-3 text-slate-300 text-xs">{p.category}</td>
                  <td className="px-5 py-3 text-right text-atc-green tabular-nums">${Number(p.price || 0).toFixed(2)}</td>
                  <td className="px-5 py-3 text-slate-400 text-xs">{p.unit}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.in_stock ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
                      {p.in_stock ? "In stock" : "Out"}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <Switch checked={p.is_published} onCheckedChange={async (v) => { await Portal.products.update(p.id, { is_published: v }); load(); }} data-testid={`portal-product-publish-${p.id}`} />
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => { setForm({ ...emptyProduct, ...p }); setEdit(p.id); }} className="p-1.5 text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(p)} className="p-1.5 text-slate-400 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-xl bg-slate-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>{edit === "new" ? "Add product" : "Edit product"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Product name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-slate-900 border-white/10" data-testid="portal-product-name" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="SKU" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} className="bg-slate-900 border-white/10" data-testid="portal-product-sku" />
              <Input placeholder="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="bg-slate-900 border-white/10" />
              <Input type="number" placeholder="Sort" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} className="bg-slate-900 border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <Input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="pl-7 bg-slate-900 border-white/10" data-testid="portal-product-price" />
              </div>
              <select value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} className="px-3 py-2 rounded-lg bg-slate-900 border border-white/10 text-sm text-white" data-testid="portal-product-unit">
                <option value="each">each</option>
                <option value="case">case</option>
                <option value="pallet">pallet</option>
                <option value="set">set</option>
                <option value="pair">pair</option>
                <option value="box">box</option>
              </select>
            </div>
            <Textarea rows={2} placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="bg-slate-900 border-white/10" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">Image (optional)</div>
              <MediaUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} kind="image" testId="portal-product-image" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.in_stock} onCheckedChange={(v) => setForm((f) => ({ ...f, in_stock: v }))} data-testid="portal-product-stock" /> In stock
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))} /> Published
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="px-4 py-2 text-slate-400 text-sm">Cancel</button>
            <button onClick={save} className="px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="portal-product-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** ------------------------- ORDERS ------------------------- */
const OrdersTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await Portal.orders.listAll()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (o, status) => {
    try { await Portal.orders.updateStatus(o.id, status); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <p className="text-xs text-slate-400 mb-5">Reorder requests submitted by portal members.</p>
      {loading ? <div className="text-slate-400">Loading…</div> : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <div key={o.id} className="glass rounded-xl p-5" data-testid={`portal-order-${o.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-white font-semibold">{o.name || o.email}</div>
                  <div className="text-xs text-slate-400">{o.email} · {new Date(o.created_at).toLocaleString()}</div>
                  {o.contact_phone && <div className="text-xs text-slate-400">Phone: {o.contact_phone}</div>}
                  {o.delivery_address && <div className="text-xs text-slate-400">Ship to: {o.delivery_address}</div>}
                </div>
                <select
                  value={o.status}
                  onChange={(e) => setStatus(o, e.target.value)}
                  className="px-3 py-1.5 rounded-full bg-slate-900 border border-white/10 text-xs text-white"
                  data-testid={`portal-order-status-${o.id}`}
                >
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="fulfilled">Fulfilled</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <ul className="mt-3 text-sm text-slate-200 space-y-0.5">
                {o.items.map((i, ix) => (
                  <li key={ix} className="flex items-baseline justify-between gap-4">
                    <span>{i.quantity}× {i.name}{i.sku ? <span className="text-slate-500 text-xs ml-1">({i.sku})</span> : null}</span>
                    {i.unit_price > 0 && (
                      <span className="text-atc-green text-xs tabular-nums">${(Number(i.unit_price) * Number(i.quantity || 1)).toFixed(2)}</span>
                    )}
                  </li>
                ))}
              </ul>
              {o.items.some((i) => i.unit_price > 0) && (
                <div className="mt-2 pt-2 border-t border-white/5 flex justify-between text-xs">
                  <span className="text-slate-400 uppercase tracking-[0.14em]">Estimated total</span>
                  <span className="text-atc-green font-semibold tabular-nums">${o.items.reduce((s, i) => s + (Number(i.unit_price) || 0) * (Number(i.quantity) || 1), 0).toFixed(2)}</span>
                </div>
              )}
              {o.notes && <div className="mt-3 text-xs text-slate-400 italic">{o.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** ------------------------- QUESTIONS & ARTICLES ------------------------- */
const emptyArticle = { title: "", body_html: "", category: "General", sort_order: 0, is_published: true };
const QATab = () => {
  const [tab, setTab] = useState("questions");
  const [questions, setQuestions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [q, a] = await Promise.all([Portal.questions.listAll(), Portal.articles.list()]);
      setQuestions(q); setArticles(a);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveArticle = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      if (edit === "new") await Portal.articles.create(form);
      else await Portal.articles.update(edit, form);
      toast.success("Saved");
      setEdit(null); load();
    } catch { toast.error("Failed"); }
  };
  const removeArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await Portal.articles.remove(a.id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const sendReply = async () => {
    if (!replyText.trim()) return;
    try {
      await Portal.questions.reply(replyingTo.id, { reply: replyText.trim(), status: "answered" });
      toast.success("Reply sent");
      setReplyingTo(null); setReplyText(""); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-5">
        <button onClick={() => setTab("questions")} className={`text-sm ${tab === "questions" ? "text-white font-semibold" : "text-slate-400"}`} data-testid="qa-subtab-questions">
          Member questions ({questions.filter((q) => q.status !== "answered").length} open)
        </button>
        <button onClick={() => setTab("articles")} className={`text-sm ${tab === "articles" ? "text-white font-semibold" : "text-slate-400"}`} data-testid="qa-subtab-articles">
          Knowledge base articles
        </button>
      </div>

      {tab === "questions" ? (
        loading ? <div className="text-slate-400">Loading…</div> : questions.length === 0 ? (
          <div className="glass rounded-xl p-10 text-center text-slate-400">No questions yet.</div>
        ) : (
          <div className="space-y-3">
            {questions.map((q) => (
              <div key={q.id} className="glass rounded-xl p-5" data-testid={`portal-question-${q.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-white font-semibold text-sm">{q.subject}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${q.status === "answered" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{q.status}</span>
                </div>
                <div className="text-xs text-slate-400">{q.name || q.email} · {new Date(q.created_at).toLocaleString()}</div>
                <div className="text-sm text-slate-200 mt-2 whitespace-pre-wrap">{q.body}</div>
                {q.reply && <div className="mt-3 pt-3 border-t border-white/5 text-sm text-atc-green whitespace-pre-wrap"><div className="text-[10px] uppercase tracking-[0.18em] text-atc-blue mb-1">You replied</div>{q.reply}</div>}
                <button onClick={() => { setReplyingTo(q); setReplyText(q.reply || ""); }} className="mt-3 text-xs text-atc-blue hover:text-white" data-testid={`portal-question-reply-${q.id}`}>
                  {q.reply ? "Edit reply" : "Reply"} →
                </button>
              </div>
            ))}
          </div>
        )
      ) : (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => { setForm(emptyArticle); setEdit("new"); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full brand-gradient text-white text-xs font-semibold" data-testid="portal-article-add"><Plus className="h-3.5 w-3.5" /> New article</button>
          </div>
          {loading ? <div className="text-slate-400">Loading…</div> : articles.length === 0 ? (
            <div className="glass rounded-xl p-10 text-center text-slate-400">No articles yet.</div>
          ) : (
            <div className="space-y-2">
              {articles.map((a) => (
                <div key={a.id} className="glass rounded-xl p-4 flex items-center gap-4" data-testid={`portal-article-${a.id}`}>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-atc-blue">{a.category}</div>
                    <div className="text-white text-sm font-semibold">{a.title}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.is_published ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{a.is_published ? "Live" : "Draft"}</span>
                  <button onClick={() => { setForm({ ...emptyArticle, ...a }); setEdit(a.id); }} className="p-1 text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => removeArticle(a)} className="p-1 text-slate-400 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl bg-slate-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>{edit === "new" ? "New article" : "Edit article"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-slate-900 border-white/10" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="bg-slate-900 border-white/10" />
              <Input type="number" placeholder="Sort" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} className="bg-slate-900 border-white/10" />
            </div>
            <RichTextEditor value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} placeholder="Write the answer / article body…" minHeight={220} />
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))} /> Published
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="px-4 py-2 text-slate-400 text-sm">Cancel</button>
            <button onClick={saveArticle} className="px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="portal-article-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replyingTo} onOpenChange={(o) => !o && setReplyingTo(null)}>
        <DialogContent className="max-w-lg bg-slate-950 border-white/10 text-white">
          <DialogHeader><DialogTitle>Reply to {replyingTo?.name || replyingTo?.email}</DialogTitle></DialogHeader>
          {replyingTo && <div className="text-xs text-slate-400 mb-3 border-l-2 border-white/10 pl-3">{replyingTo.body}</div>}
          <Textarea rows={6} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type your reply…" className="bg-slate-900 border-white/10" data-testid="portal-reply-text" />
          <DialogFooter>
            <button onClick={() => setReplyingTo(null)} className="px-4 py-2 text-slate-400 text-sm">Cancel</button>
            <button onClick={sendReply} className="px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="portal-reply-send">Send reply</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** ------------------------- MESSAGES ------------------------- */
const MessagesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await Portal.messages.listAll()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (m) => {
    try { await Portal.messages.markRead(m.id); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <p className="text-xs text-slate-400 mb-5">Direct messages sent from the portal Contact tab.</p>
      {loading ? <div className="text-slate-400">Loading…</div> : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">No messages yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} className={`glass rounded-xl p-5 ${!m.is_read ? "border-atc-blue/30" : ""}`} data-testid={`portal-msg-${m.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="text-white font-semibold text-sm">{m.subject || "(no subject)"}</div>
                  <div className="text-xs text-slate-400">{m.name || m.email} · {new Date(m.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  {!m.is_read && <span className="text-[10px] px-2 py-0.5 rounded-full bg-atc-blue/20 text-atc-blue uppercase tracking-wider">New</span>}
                  {!m.is_read && (
                    <button onClick={() => markRead(m)} className="text-xs text-slate-400 hover:text-white" data-testid={`portal-msg-read-${m.id}`}>Mark read</button>
                  )}
                </div>
              </div>
              <div className="text-sm text-slate-200 mt-3 whitespace-pre-wrap">{m.body}</div>
              <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "your message")}`} className="mt-3 inline-block text-xs text-atc-blue hover:text-white">Reply by email →</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** ------------------------- ROOT ------------------------- */
const PortalManager = () => (
  <div className="p-10" data-testid="portal-manager">
    <div className="mb-8">
      <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">CMS</div>
      <h1 className="font-display font-black text-4xl text-white">Dealer Portal</h1>
      <p className="text-slate-400 text-sm mt-2">Manage invited users, training content, and support inbox.</p>
    </div>

    <Tabs defaultValue="invites">
      <TabsList className="bg-slate-950 border border-white/10 flex-wrap h-auto">
        <TabsTrigger value="invites" data-testid="tab-invites"><Mail className="h-3.5 w-3.5 mr-1.5" /> Invites</TabsTrigger>
        <TabsTrigger value="videos" data-testid="tab-videos"><PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Videos</TabsTrigger>
        <TabsTrigger value="docs" data-testid="tab-docs"><FileText className="h-3.5 w-3.5 mr-1.5" /> Manuals</TabsTrigger>
        <TabsTrigger value="products" data-testid="tab-products"><DollarSign className="h-3.5 w-3.5 mr-1.5" /> Product Pricing</TabsTrigger>
        <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingCart className="h-3.5 w-3.5 mr-1.5" /> Orders</TabsTrigger>
        <TabsTrigger value="qa" data-testid="tab-qa"><MessagesSquare className="h-3.5 w-3.5 mr-1.5" /> Q&amp;A</TabsTrigger>
        <TabsTrigger value="messages" data-testid="tab-messages"><Send className="h-3.5 w-3.5 mr-1.5" /> Messages</TabsTrigger>
      </TabsList>
      <TabsContent value="invites" className="mt-6"><InvitesTab /></TabsContent>
      <TabsContent value="videos" className="mt-6"><VideosTab /></TabsContent>
      <TabsContent value="docs" className="mt-6"><DocsTab /></TabsContent>
      <TabsContent value="products" className="mt-6"><ProductsTab /></TabsContent>
      <TabsContent value="orders" className="mt-6"><OrdersTab /></TabsContent>
      <TabsContent value="qa" className="mt-6"><QATab /></TabsContent>
      <TabsContent value="messages" className="mt-6"><MessagesTab /></TabsContent>
    </Tabs>
  </div>
);

export default PortalManager;
